import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  User, Building2, Mail, Phone, ChevronDown,
  MessageSquare, AlertCircle, CheckCircle2, Loader2, Package,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { productsData } from '@/data/index';

// ── 类型 ──────────────────────────────────────────────
export type InquiryMode = 'inquiry' | 'sample';

interface FormFields {
  name: string;
  company: string;
  email: string;
  phone: string;
  material: string;
  message: string;
  // 样品专属字段
  sampleSpec: string;
  sampleQty: string;
  samplePurpose: string;
}

type FieldErrors = Partial<Record<keyof FormFields, string>>;
type TouchedFields = Partial<Record<keyof FormFields, boolean>>;

export interface InquiryFormProps {
  mode?: InquiryMode;
  onSuccess?: () => void;
}

// ── 验证 ──────────────────────────────────────────────
type InquiryFormT = { requiredName: string; invalidEmail: string; noEmail: string; requiredPhone: string; invalidPhone: string; requiredMsg: string; minMsg: string; };
function makeValidator(vt: InquiryFormT, mode: InquiryMode) {
  return function validate(f: FormFields): FieldErrors {
    const err: FieldErrors = {};
    if (!f.name.trim()) err.name = vt.requiredName;
    if (!f.email.trim()) { err.email = vt.noEmail; }
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) { err.email = vt.invalidEmail; }
    if (!f.phone.trim()) { err.phone = vt.requiredPhone; }
    else if (!/^[\d\s+\-().]{7,20}$/.test(f.phone)) { err.phone = vt.invalidPhone; }
    // inquiry 模式要求备注，sample 模式备注可选
    if (mode === 'inquiry') {
      if (!f.message.trim()) { err.message = vt.requiredMsg; }
      else if (f.message.trim().length < 10) { err.message = vt.minMsg; }
    }
    return err;
  };
}

// ── 主组件 ────────────────────────────────────────────
const ZH_FORM_T: InquiryFormT = {
  requiredName: '请输入您的姓名',
  invalidEmail: '请输入有效的邮箱地址',
  noEmail: '请输入邮箱地址',
  requiredPhone: '请输入联系电话',
  invalidPhone: '请输入有效的电话号码',
  requiredMsg: '请输入咨询内容',
  minMsg: '咨询内容至少需要10个字符',
};

export function InquiryForm({ mode = 'inquiry', onSuccess }: InquiryFormProps) {
  const validate = makeValidator(ZH_FORM_T, mode);
  const EMPTY: FormFields = { name: '', company: '', email: '', phone: '', material: '', message: '', sampleSpec: '', sampleQty: '', samplePurpose: '' };

  const [fields, setFields] = useState<FormFields>(EMPTY);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [touched, setTouched] = useState<TouchedFields>({});
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [serverErr, setServerErr] = useState('');

  // 字段更新 + 实时校验（已触碰过的字段）
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    const next = { ...fields, [name]: value };
    setFields(next);
    if (touched[name as keyof FormFields]) {
      setErrors(prev => ({ ...prev, [name]: validate(next)[name as keyof FormFields] }));
    }
  };

  // 失焦时标记已触碰并校验
  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    setErrors(prev => ({ ...prev, [name]: validate(fields)[name as keyof FormFields] }));
  };

  // 构建最终写入的 message（sample 模式自动拼接样品信息）
  const buildMessage = (): string => {
    if (mode === 'inquiry') return fields.message.trim();
    const lines: string[] = ['【样品申请】'];
    if (fields.material) lines.push(`申请材质：${fields.material}`);
    if (fields.sampleSpec.trim()) lines.push(`规格要求：${fields.sampleSpec.trim()}`);
    if (fields.sampleQty.trim()) lines.push(`申请数量：${fields.sampleQty.trim()}`);
    if (fields.samplePurpose.trim()) lines.push(`用途说明：${fields.samplePurpose.trim()}`);
    if (fields.message.trim()) lines.push(`补充说明：${fields.message.trim()}`);
    return lines.join('\n');
  };

  // 提交
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const allTouched = Object.keys(fields).reduce((a, k) => ({ ...a, [k]: true }), {} as TouchedFields);
    setTouched(allTouched);
    const errs = validate(fields);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setStatus('loading');
    try {
      const _now = new Date();
      const _dateStr = _now.toISOString().slice(0,10).replace(/-/g,'');
      const _startOfDay = `${_now.toISOString().slice(0,10)}T00:00:00`;
      const { count: _todayCount } = await supabase
        .from('inquiries')
        .select('id', { count: 'exact', head: true })
        .gte('created_at', _startOfDay);
      const _seq = String((_todayCount ?? 0) + 1).padStart(3, '0');
      const _rand4 = String(Math.floor(1000 + Math.random() * 9000));
      const biz_no = `BY-${_dateStr}-${_seq}-${_rand4}`;
      const finalMessage = buildMessage();
      const { error } = await supabase.from('inquiries').insert({
        name: fields.name.trim(),
        company: fields.company.trim() || null,
        email: fields.email.trim().toLowerCase(),
        phone: fields.phone.trim(),
        material: fields.material || null,
        message: finalMessage,
        biz_no,
        notes: mode === 'sample' ? '来源：官网样品申请表单' : '来源：官网在线咨询表单',
      });
      if (error) throw error;
      setStatus('success');
      if (onSuccess) onSuccess();
      // 自动录入客户
      try {
        const phone = fields.phone.trim();
        const email = fields.email.trim().toLowerCase();
        const company = fields.company.trim() || null;
        let isDuplicate = false;
        if (phone) {
          const { data: d1 } = await supabase.from('customers').select('id').eq('phone', phone).limit(1);
          if (d1 && d1.length > 0) isDuplicate = true;
        }
        if (!isDuplicate && email) {
          const { data: d2 } = await supabase.from('customers').select('id').eq('email', email).limit(1);
          if (d2 && d2.length > 0) isDuplicate = true;
        }
        if (!isDuplicate && company) {
          const { data: d3 } = await supabase.from('customers').select('id').eq('company', company).limit(1);
          if (d3 && d3.length > 0) isDuplicate = true;
        }
        if (!isDuplicate) {
          await supabase.from('customers').insert([{
            name: fields.name.trim(),
            company,
            phone: phone || null,
            email: email || null,
            notes: mode === 'sample' ? '来源：官网样品申请表单' : '来源：官网在线咨询表单',
          }]);
        }
      } catch (custErr) {
        console.warn('客户自动录入失败（不影响线索提交）:', custErr);
      }
    } catch (err: unknown) {
      console.error('Inquiry submit error:', err);
      setServerErr('提交失败，请稍后重试或直接拨打电话联系我们');
      setStatus('error');
    }
  };

  // 重置
  const reset = () => { setFields(EMPTY); setErrors({}); setTouched({}); setStatus('idle'); setServerErr(''); };

  // ── 成功页面 ──
  if (status === 'success') {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        className="flex flex-col items-center justify-center text-center py-16 px-8"
      >
        <div className="w-20 h-20 rounded-full bg-primary/10 border-2 border-primary flex items-center justify-center mb-6">
          <CheckCircle2 className="w-10 h-10 text-primary" />
        </div>
        <h3 className="text-2xl font-bold mb-3">
          {mode === 'sample' ? '样品申请已提交' : '询价已提交'}
        </h3>
        <p className="text-muted-foreground mb-2">
          {mode === 'sample'
            ? '感谢您的申请，我们将在2个工作日内安排寄送并与您联系。'
            : '感谢您的咨询，我们的技术工程师将在1个工作日内与您联系。'}
        </p>
        <p className="text-sm text-muted-foreground mb-8">
          如需紧急支持，请直接致电：{' '}
          <a href="tel:13426031477" className="font-mono font-bold text-primary hover:underline">
            13426031477
          </a>
        </p>
        <button
          onClick={reset}
          className="px-6 py-2 text-sm font-medium rounded-sm border border-border text-muted-foreground hover:border-primary hover:text-primary transition-colors"
        >
          再次提交
        </button>
      </motion.div>
    );
  }

  // ── 表单主体 ──
  return (
    <motion.form
      onSubmit={handleSubmit}
      noValidate
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="space-y-5"
    >
      {/* 姓名 + 公司 */}
      <div className="grid md:grid-cols-2 gap-4">
        <Field
          icon={<User className="w-4 h-4" />}
          label="姓名" required
          name="name" type="text"
          placeholder="您的姓名"
          value={fields.name}
          error={touched.name ? errors.name : undefined}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        <Field
          icon={<Building2 className="w-4 h-4" />}
          label="公司"
          name="company" type="text"
          placeholder="公司名称（选填）"
          value={fields.company}
          onChange={handleChange}
        />
      </div>

      {/* 邮箱 + 电话 */}
      <div className="grid md:grid-cols-2 gap-4">
        <Field
          icon={<Mail className="w-4 h-4" />}
          label="邮箱" required
          name="email" type="email"
          placeholder="your@company.com"
          value={fields.email}
          error={touched.email ? errors.email : undefined}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        <Field
          icon={<Phone className="w-4 h-4" />}
          label="电话" required
          name="phone" type="tel"
          placeholder="+86 138 0000 0000"
          value={fields.phone}
          error={touched.phone ? errors.phone : undefined}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </div>

      {/* 材料下拉 */}
      <div>
        <label className="block text-sm font-medium mb-1.5">
          {mode === 'sample' ? '申请材质' : '产品材质'}
          {mode === 'sample' && <span className="text-muted-foreground text-xs ml-1">（选填）</span>}
        </label>
        <div className="relative">
          <select
            name="material"
            value={fields.material}
            onChange={handleChange}
            className="w-full appearance-none pl-4 pr-9 py-2.5 rounded-sm text-sm outline-none transition-colors bg-input border border-border text-foreground focus:border-primary focus:ring-1 focus:ring-primary"
          >
            <option value="">请选择材质（选填）</option>
            {productsData.map(p => (
              <option key={p.id} value={p.code}>
                {p.code} — {p.name}
              </option>
            ))}
            <option value="OTHER">其他 / 不确定</option>
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        </div>
      </div>

      {/* 样品专属字段 */}
      {mode === 'sample' && (
        <div className="space-y-4 p-4 rounded-sm border border-primary/20 bg-primary/5">
          <div className="flex items-center gap-2 mb-1">
            <Package className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary">样品信息</span>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium mb-1 text-muted-foreground">规格要求</label>
              <input
                name="sampleSpec" type="text"
                placeholder="如：3mm × 200mm × 200mm"
                value={fields.sampleSpec}
                onChange={handleChange}
                className="w-full px-3 py-2 rounded-sm text-sm bg-input border border-border outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1 text-muted-foreground">申请数量</label>
              <input
                name="sampleQty" type="text"
                placeholder="如：2片 / 0.5kg"
                value={fields.sampleQty}
                onChange={handleChange}
                className="w-full px-3 py-2 rounded-sm text-sm bg-input border border-border outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1 text-muted-foreground">用途说明</label>
            <input
              name="samplePurpose" type="text"
              placeholder="如：用于汽车密封件验证测试"
              value={fields.samplePurpose}
              onChange={handleChange}
              className="w-full px-3 py-2 rounded-sm text-sm bg-input border border-border outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
          </div>
        </div>
      )}

      {/* 咨询内容 / 补充说明 */}
      <div>
        <label className="block text-sm font-medium mb-1.5">
          {mode === 'sample' ? '补充说明' : <>咨询内容 <span className="text-primary">*</span></>}
        </label>
        <div className="relative">
          <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-muted-foreground pointer-events-none" />
          <textarea
            name="message"
            value={fields.message}
            onChange={handleChange}
            onBlur={handleBlur}
            rows={5}
            placeholder="请描述您的需求，包括使用工况、尺寸规格、数量等信息...（至少10字）"
            className={`w-full pl-10 pr-4 py-2.5 rounded-sm text-sm bg-input border outline-none transition-colors resize-none focus:border-primary focus:ring-1 focus:ring-primary ${touched.message && errors.message ? 'border-destructive' : 'border-border'}`}
          />
        </div>
        <AnimatePresence>
          {touched.message && errors.message && <ErrMsg msg={errors.message} />}
        </AnimatePresence>
      </div>

      {/* 服务端错误 */}
      <AnimatePresence>
        {status === 'error' && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="flex items-start gap-2 p-3 rounded-sm text-sm bg-destructive/10 border border-destructive/30 text-destructive"
          >
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            {serverErr}
          </motion.div>
        )}
      </AnimatePresence>

      {/* 提交按钮 */}
      <button
        type="submit"
        disabled={status === 'loading'}
        className="w-full flex items-center justify-center gap-2 py-3 text-sm font-bold rounded-sm bg-primary text-primary-foreground hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {status === 'loading'
          ? <><Loader2 className="w-4 h-4 animate-spin" />提交中...</>
          : mode === 'sample' ? '提交样品申请' : '提交询价'}
      </button>

      <p className="text-xs text-center text-muted-foreground">
        提交即表示您同意我们的{' '}
        <a href="/privacy" className="underline hover:text-foreground transition-colors">隐私政策</a>
        ，我们将保护您的个人信息安全。
      </p>
    </motion.form>
  );
}

// ── 复用：单行输入框 ────────────────────────────────────
interface FieldProps {
  icon: React.ReactNode;
  label: string;
  required?: boolean;
  name: string;
  type: string;
  placeholder: string;
  value: string;
  error?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

function Field({ icon, label, required, name, type, placeholder, value, error, onChange, onBlur }: FieldProps) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1.5">
        {label}{required && <span className="text-primary"> *</span>}
      </label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
          {icon}
        </span>
        <input
          name={name}
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          className={`w-full pl-10 pr-4 py-2.5 rounded-sm text-sm bg-input border outline-none transition-colors focus:border-primary focus:ring-1 focus:ring-primary ${error ? 'border-destructive' : 'border-border'}`}
        />
      </div>
      <AnimatePresence>
        {error && <ErrMsg msg={error} />}
      </AnimatePresence>
    </div>
  );
}

// ── 复用：错误提示动画 ──────────────────────────────────
function ErrMsg({ msg }: { msg: string }) {
  return (
    <motion.p
      initial={{ opacity: 0, y: -4, height: 0 }}
      animate={{ opacity: 1, y: 0, height: 'auto' }}
      exit={{ opacity: 0, y: -4, height: 0 }}
      transition={{ duration: 0.2 }}
      className="flex items-center gap-1 text-xs mt-1 text-destructive"
    >
      <AlertCircle className="w-3 h-3 flex-shrink-0" />
      {msg}
    </motion.p>
  );
}
